import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { EmptyValue } from '@/components/common/EmptyValue';
import { RowActionButton, RowEditDeleteButtons } from '@/components/common/RowActions';
import { Button } from '@/components/ui/button';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate } from '@/utils/date';
import { OPTION_DETAIL_SEPARATOR } from '@/utils/filters';
import { formatNumber } from '@/utils/number';
import { BatchStageBadge } from '@/features/publish/components/BatchStageBadge';
import type {
  DonationBatch,
  DonationBatchMember,
  DonationBudget,
  DonationRequisition,
  MarketProject,
} from '../types/marketPromotionP2.types';

const dash = (value: string | null): string =>
  value === null || value === '' ? EMPTY_VALUE : value;
const money = (value: number): string => formatNumber(Math.round(value));
const day = (value: string | null): string =>
  value === null ? EMPTY_VALUE : formatDisplayDate(value);

interface RowActions<TRow> {
  onEdit: (row: TRow) => void;
  onDelete?: (row: TRow) => void;
}

function actionsColumn<TRow>(
  actions: RowActions<TRow>,
  getItemName: (row: TRow) => string,
): ColumnDef<TRow, unknown> {
  return createActionsColumn<TRow>((item) => (
    <RowEditDeleteButtons
      item={item}
      itemName={getItemName(item)}
      onEdit={actions.onEdit}
      onDelete={actions.onDelete}
    />
  ));
}

export function getMarketProjectColumns(
  actions?: RowActions<MarketProject>,
): ColumnDef<MarketProject, unknown>[] {
  const columns: ColumnDef<MarketProject, unknown>[] = [
    createSerialColumn<MarketProject>(),
    {
      accessorKey: 'name',
      header: 'Project',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">{dash(row.original.name)}</WrappingCellText>
      ),
    },
    { id: 'code', header: 'Code', cell: ({ row }) => dash(row.original.code) },
    { id: 'class', header: 'Class', cell: ({ row }) => dash(row.original.className) },
    { id: 'start', header: 'Start', cell: ({ row }) => day(row.original.startDate) },
    { id: 'deadline', header: 'Deadline', cell: ({ row }) => day(row.original.deadlineDate) },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
  ];

  if (actions) columns.push(actionsColumn(actions, (project) => project.name ?? 'project'));
  return columns;
}

export function getDonationBudgetColumns(
  actions?: RowActions<DonationBudget>,
): ColumnDef<DonationBudget, unknown>[] {
  const columns: ColumnDef<DonationBudget, unknown>[] = [
    createSerialColumn<DonationBudget>(),
    {
      id: 'member',
      header: 'Member',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.userName)}
          primaryClassName="font-medium"
          secondary={[row.original.designation, row.original.department]
            .filter(Boolean)
            .join(' · ')}
        />
      ),
    },
    {
      id: 'where',
      header: 'Thana / District',
      cell: ({ row }) =>
        [row.original.thanaName, row.original.districtName].filter(Boolean).join(', ') ||
        EMPTY_VALUE,
    },
    { id: 'item', header: 'Donation Item', cell: ({ row }) => dash(row.original.itemName) },
    {
      id: 'budget',
      header: 'Budget',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.budgetAmount)}</span>,
    },
    {
      id: 'disburse',
      header: 'Disbursed',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.disburseAmount)}</span>,
    },
    { id: 'budgetDate', header: 'Budget Date', cell: ({ row }) => day(row.original.budgetDate) },
    { id: 'targetDate', header: 'Target Date', cell: ({ row }) => day(row.original.targetDate) },
  ];

  if (actions) {
    columns.push(actionsColumn(actions, (budget) => `budget of ${budget.userName ?? 'this AM'}`));
  }
  return columns;
}

export function getDonationBatchColumns(
  onOpen: (batch: DonationBatch) => void,
  action?: {
    label: string;
    onClick: (batch: DonationBatch) => void;
    when: (batch: DonationBatch) => boolean;
  },
): ColumnDef<DonationBatch, unknown>[] {
  const columns: ColumnDef<DonationBatch, unknown>[] = [
    createSerialColumn<DonationBatch>(),
    {
      id: 'batch',
      header: 'Batch',
      cell: ({ row }) => (
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 font-medium"
          onClick={() => onOpen(row.original)}
          aria-label={`Members of batch ${row.original.legacyId ?? row.original.batchId}`}
        >
          #{row.original.legacyId ?? row.original.batchId}
        </Button>
      ),
    },
    { id: 'item', header: 'Donation Item', cell: ({ row }) => dash(row.original.itemName) },
    {
      id: 'covering',
      header: 'Covering Work',
      cell: ({ row }) => `${day(row.original.trnDateFrom)} – ${day(row.original.trnDateTo)}`,
    },
    {
      id: 'published',
      header: 'Published',
      cell: ({ row }) => (
        <StackedCellText
          primary={day(row.original.publishDate)}
          secondary={row.original.publishedBy}
        />
      ),
    },
    {
      id: 'amount',
      header: 'Amount',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.promoItemQty)}</span>,
    },
    {
      id: 'stage',
      header: 'Stage',
      cell: ({ row }) => <BatchStageBadge stage={row.original.stage} />,
    },
  ];

  if (action) {
    columns.push(
      createActionsColumn<DonationBatch>((batch) => {
        if (!action.when(batch)) return <EmptyValue />;
        return (
          <RowActionButton
            label={action.label}
            ariaLabel={`${action.label} batch ${batch.legacyId ?? batch.batchId}`}
            onClick={() => action.onClick(batch)}
          />
        );
      }),
    );
  }

  return columns;
}

export function getDonationMemberColumns(): ColumnDef<DonationBatchMember, unknown>[] {
  return [
    {
      id: 'member',
      header: 'ZM Name',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.fullName)}
          primaryClassName="font-medium"
          secondary={[row.original.hrEmpId, row.original.designationName]
            .filter(Boolean)
            .join(OPTION_DETAIL_SEPARATOR)}
        />
      ),
    },
    { id: 'reporting', header: 'Reporting', cell: ({ row }) => dash(row.original.reportingName) },
    { id: 'account', header: 'Account No', cell: ({ row }) => dash(row.original.accountNumber) },
    { id: 'mobile', header: 'Mobile', cell: ({ row }) => dash(row.original.mobile) },
    {
      id: 'where',
      header: 'Division / District / Thana',
      cell: ({ row }) =>
        [row.original.divisionName, row.original.districtName, row.original.thanaName]
          .filter(Boolean)
          .join(', ') || '—',
    },
    {
      id: 'donations',
      header: 'Donations',
      cell: ({ row }) => <span className="tabular-nums">{row.original.donationCount}</span>,
    },
    {
      id: 'amount',
      header: 'Total',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.donationAmount)}</span>,
    },
  ];
}

export function getRequisitionColumns(
  actions?: RowActions<DonationRequisition>,
): ColumnDef<DonationRequisition, unknown>[] {
  const columns: ColumnDef<DonationRequisition, unknown>[] = [
    createSerialColumn<DonationRequisition>(),
    {
      id: 'institute',
      header: 'Institute',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.instituteName)}
          primaryClassName="font-medium"
          secondary={[row.original.instituteCode, row.original.instituteEiin]
            .filter(Boolean)
            .join(OPTION_DETAIL_SEPARATOR)}
        />
      ),
    },
    {
      id: 'teacher',
      header: 'Teacher',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.teacherName)}
          secondary={row.original.teacherPhone}
        />
      ),
    },
    { id: 'specimen', header: 'Specimen', cell: ({ row }) => dash(row.original.specimenName) },
    { id: 'bookType', header: 'Book Type', cell: ({ row }) => dash(row.original.bookTypeName) },
    {
      id: 'students',
      header: 'Students',
      cell: ({ row }) => <span className="tabular-nums">{row.original.studentQty ?? '—'}</span>,
    },
    {
      id: 'target',
      header: 'Target Sale',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.targetSaleQty)}</span>,
    },
    {
      id: 'budget',
      header: 'Budget',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.budgetAmount)}</span>,
    },
    {
      id: 'signoff',
      header: 'Sign-off',
      cell: ({ row }) => {
        const stages = [
          row.original.isChecked ? 'Checked' : null,
          row.original.isApproved ? 'Approved' : null,
          row.original.isAudit ? 'Audited' : null,
        ].filter(Boolean);
        return stages.length === 0 ? (
          <span className="text-xs text-muted-foreground">Pending</span>
        ) : (
          <span className="text-xs">{stages.join(' · ')}</span>
        );
      },
    },
    {
      id: 'entered',
      header: 'Entered',
      cell: ({ row }) => (
        <StackedCellText primary={day(row.original.enteredAt)} secondary={row.original.enteredBy} />
      ),
    },
  ];

  if (actions) {
    columns.push(
      actionsColumn(actions, (requisition) => requisition.specimenName ?? 'requisition'),
    );
  }
  return columns;
}
